import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Customer {
  id: string;
  business_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  full_name: string;
  created_at: string;
  updated_at: string;
  lifecycle_stage: 'lead' | 'customer' | 'advocate' | 'lost';
  lead_source: string | null;
  lead_score: number;
  last_engagement_at: string | null;
  tags?: any[];
  sources: string[];
  totalReviews: number;
  averageRating: number | null;
  loyaltyPrograms: string[];
  unsubscribed: boolean;
  unsubscribed_at: string | null;
  last_emailed_at: string | null;
  email_count: number;
}

interface UseCustomersOptions {
  businessId: string;
  lifecycleFilter?: string;
  sourceFilter?: string;
  sortBy?: string;
}

interface UseCustomersReturn {
  customers: Customer[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useCustomers = ({
  businessId,
  lifecycleFilter = 'all',
  sourceFilter = 'all',
  sortBy = 'created_at_desc',
}: UseCustomersOptions): UseCustomersReturn => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCustomers = async () => {
    if (!businessId) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [customersRes, contactsRes] = await Promise.all([
        supabase.from('customers').select(`
          *,
          reviews!customer_id(id, form_id, rating, created_at, review_forms(id, title)),
          passes!customer_uuid(id, loyalty_program_id, created_at, loyalty_programs(id, name)),
          dotypos_customer_mapping!customer_id(id, loyalty_program_id, created_at, loyalty_programs(id, name))
        `).eq('business_id', businessId),
        supabase.from('contacts').select(`
          email, last_emailed_at, email_count,
          email_preferences(unsubscribed, unsubscribed_at)
        `).eq('business_id', businessId)
      ]);

      if (customersRes.error) throw customersRes.error;

      const contactStatusMap = new Map<string, {
        unsubscribed: boolean;
        unsubscribed_at: string | null;
        last_emailed_at: string | null;
        email_count: number;
      }>();
      contactsRes.data?.forEach((contact: any) => {
        if (contact.email) {
          contactStatusMap.set(contact.email.toLowerCase(), {
            unsubscribed: contact.email_preferences?.unsubscribed || false,
            unsubscribed_at: contact.email_preferences?.unsubscribed_at || null,
            last_emailed_at: contact.last_emailed_at || null,
            email_count: contact.email_count || 0,
          });
        }
      });

      const contactsMap = new Map<string, Customer>();
      const customersData = customersRes.data;

      customersData?.forEach((c: any) => {
        const customerId = c.id;
        const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown';
        const emailKey = c.email ? c.email.toLowerCase() : '';
        const contactStatus = contactStatusMap.get(emailKey) || {
          unsubscribed: false, unsubscribed_at: null, last_emailed_at: null, email_count: 0
        };

        if (!contactsMap.has(customerId)) {
          contactsMap.set(customerId, {
            id: c.id,
            business_id: c.business_id,
            email: c.email,
            first_name: c.first_name,
            last_name: c.last_name,
            phone: c.phone,
            full_name: fullName,
            created_at: c.created_at,
            updated_at: c.updated_at,
            lifecycle_stage: 'customer',
            lead_source: null,
            lead_score: 0,
            last_engagement_at: c.updated_at,
            tags: [],
            sources: [],
            totalReviews: 0,
            averageRating: null,
            loyaltyPrograms: [],
            unsubscribed: contactStatus.unsubscribed,
            unsubscribed_at: contactStatus.unsubscribed_at,
            last_emailed_at: contactStatus.last_emailed_at,
            email_count: contactStatus.email_count,
          });
        }

        const contact = contactsMap.get(customerId)!;

        if (c.reviews?.length > 0) {
          const ratings = c.reviews.map((r: any) => r.rating).filter((r: any) => r != null);
          contact.totalReviews = c.reviews.length;
          contact.averageRating = ratings.length > 0
            ? ratings.reduce((sum: number, r: number) => sum + r, 0) / ratings.length
            : null;
          contact.lead_score += c.reviews.length * 10;

          const latestReview = c.reviews.reduce((latest: any, r: any) =>
            new Date(r.created_at) > new Date(latest.created_at) ? r : latest, c.reviews[0]);
          if (new Date(latestReview.created_at) > new Date(contact.last_engagement_at || 0)) {
            contact.last_engagement_at = latestReview.created_at;
          }
          if (!contact.lead_source) contact.lead_source = 'review';
          contact.sources.push('review');
        }

        const allPasses = [...(c.passes || []), ...(c.dotypos_customer_mapping || [])];
        if (allPasses.length > 0) {
          contact.loyaltyPrograms = allPasses.map((p: any) => p.loyalty_programs?.name).filter(Boolean);
          contact.lead_score += allPasses.length * 15;

          const latestPass = allPasses.reduce((latest: any, p: any) =>
            new Date(p.created_at) > new Date(latest.created_at) ? p : latest, allPasses[0]);
          if (new Date(latestPass.created_at) > new Date(contact.last_engagement_at || 0)) {
            contact.last_engagement_at = latestPass.created_at;
          }
          if (!contact.lead_source) contact.lead_source = 'loyalty';
          contact.sources.push('loyalty');
        }

        if (contact.totalReviews > 3 || contact.loyaltyPrograms.length > 0) {
          contact.lifecycle_stage = 'advocate';
        } else if (contact.totalReviews > 0 || contact.loyaltyPrograms.length > 0) {
          contact.lifecycle_stage = 'customer';
        } else {
          contact.lifecycle_stage = 'lead';
        }
      });

      let contactsArray = Array.from(contactsMap.values());

      const [field, direction] = sortBy.split('_');
      const isDesc = direction === 'desc';

      contactsArray.sort((a, b) => {
        const getTime = (d: string | null) => d ? new Date(d).getTime() : 0;
        switch (field) {
          case 'created': return isDesc ? getTime(b.created_at) - getTime(a.created_at) : getTime(a.created_at) - getTime(b.created_at);
          case 'engagement': return isDesc ? getTime(b.last_engagement_at) - getTime(a.last_engagement_at) : getTime(a.last_engagement_at) - getTime(b.last_engagement_at);
          case 'score': return isDesc ? b.lead_score - a.lead_score : a.lead_score - b.lead_score;
          default: return isDesc ? b.email.localeCompare(a.email) : a.email.localeCompare(b.email);
        }
      });

      if (lifecycleFilter !== 'all') contactsArray = contactsArray.filter(c => c.lifecycle_stage === lifecycleFilter);
      if (sourceFilter !== 'all') contactsArray = contactsArray.filter(c => c.sources.includes(sourceFilter));

      setCustomers(contactsArray);
    } catch (err: any) {
      setError(err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [businessId, lifecycleFilter, sourceFilter, sortBy]);

  return {
    customers,
    loading,
    error,
    refetch: fetchCustomers,
  };
};
